import { Injectable } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';

import config from '../../../../config';
import { EnglishLanguageModel } from '../../classes/language/english.model';
import { LanguageIntr } from '../../classes/language/language.interface';
import { IPreferredLanguagesKeys, LanguageTypes } from '../../models/language.model';
import { LanguageCodeToNgTranslateMapping, PatientLocaleToLanguageCodeMapping } from '../../models/translation.model';
import { DataBrokerService } from '../data-broker/data-broker.service';

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class LanguageService {
    public language: LanguageIntr;
    private currentLanguage: BehaviorSubject<IPreferredLanguagesKeys>;

    constructor(
        private data: DataBrokerService,
        private translate: TranslateService,
    ) {
        const langs = config.TRANSLATION_LANGUAGES;

        translate.addLangs(langs);
    }

    public setLanguage(language: string) {
        if (!language) return;
        if (LanguageTypes.includes(language)) {
            this.currentLanguage.next(language as any);
        } else {
            this.currentLanguage.next('en');
        }
    }

    public setPatientLocale(language: string) {
        if (!language) return;
        if (PatientLocaleToLanguageCodeMapping[language]) {
            this.setLanguage(PatientLocaleToLanguageCodeMapping[language]);
        } else if (LanguageTypes.includes(language)) {
            this.setLanguage(language);
        } else {
            this.setLanguage('en');
        }
    }

    public getCurrLanguage(): string {
        return this.language?.language_type || 'en';
    }

    public getCurrentLanguage() {
        return this.currentLanguage;
    }

    public init() {
        let initialLanguage: string = 'en';

        try {
            const language = window.location?.pathname.split('/')?.filter((p) => !!p)[0];

            if (LanguageTypes.includes(language)) {
                initialLanguage = language;
            }
        } catch (e) {}

        this.currentLanguage = new BehaviorSubject<IPreferredLanguagesKeys>(initialLanguage as any);
        this.setLanguage(initialLanguage);
        this.translate.onTranslationChange.subscribe((lang) => {
            const mappedValue = Object.keys(LanguageCodeToNgTranslateMapping).find(
                (key) => LanguageCodeToNgTranslateMapping[key] === lang.lang,
            );

            if (this.currentLanguage.value !== lang.lang && this.currentLanguage.value !== mappedValue) {
                this.setLanguage(mappedValue || lang.lang);
            }
        });
        this.monitorOnLanguageUpdate();
    }

    private monitorOnLanguageUpdate() {
        this.language = new EnglishLanguageModel();
        this.currentLanguage.pipe(untilDestroyed(this)).subscribe((lang) => {
            localStorage.setItem('lang', lang);
            this.data.publish(this.language, 'language-update');

            const newUrl = this.getLocalizedUrl(lang);
            const currentPath = window.location.pathname.split('/').filter((p) => !!p);

            if (
                LanguageTypes.includes(currentPath[0]) &&
                window.location.pathname !== newUrl &&
                lang != currentPath[0]
            ) {
                window.location.href = newUrl;
            }

            const mappedLang = LanguageCodeToNgTranslateMapping[lang] || lang;

            if (this.translate.currentLang !== mappedLang) {
                this.translate.setDefaultLang(mappedLang);
                this.translate.use(mappedLang);
                this.translate.currentLang = mappedLang;
            }
        });
    }

    private getLocalizedUrl(lang: string): string {
        const currentPath = window.location.pathname;
        const regex = new RegExp(`^\/(${LanguageTypes.join('|').replace(/-/g, '\\-')})\/`);

        if (regex.test(currentPath)) {
            return currentPath.replace(regex, `/${lang}/`);
        }

        const normalizedPath = currentPath === '/' ? '' : currentPath;

        return `/${lang}${normalizedPath}`;
    }
}
